import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useContext } from "react";
import { NavigationSettings } from "./settings";
import GrigoraCSSRenderer from "./../css-render";
import classNames from "classnames";
import icon from "./icon";
import { createPortal } from "react-dom";
import { GrigoraDashboardContext } from "../../dashboard/context";
import SVG from "react-inlinesvg";
import SVGIcons from "../../input/icons.json";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";

export const Navigation = (props) => {
  const { editorDocument = document } =
    useContext(GrigoraDashboardContext) ?? {};

  const {
    id,
    connectors: { connect, drag },
    selected,
    actions: { setProp },
    dom,
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
    dom: state.dom,
  }));

  const {
    query: { node },
  } = useEditor();

  const childrens = node(id).childNodes();
  const parents = node(id).ancestors();

  const {
    children,
    menuTrigger = "tablet",
    menuStatus = "hide",
    menuIcon = "list",
    menuIconSize = "26px",
    menuIconBorder,
    menuIconBorderRadius,
    menuIconPadding,
    menuIconColor,
    menuIconBackgroundColor,
    menuDirection = "row",
    menuWrap = "wrap",
    menuJustifyContent = "start",
    menuAlignItems = "center",
    menuGap = "1rem",
    portalPadding,
    portalGap = "1rem",
    portalType = "dropdown",
    portalTopOffset,
    portalWidth = "100vw",
    portalHeight = "auto",
    portalAlign,
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-navigation": true,
      [`navigation-${id}`]: id,
    },
    blockProps.className,
  );

  var content = (
    <>
      <div className="childrens">
        {Array.isArray(childrens) && childrens.length === 0 ? (
          <>
            <div className="empty-navigation">Empty Navigation Block.</div>
          </>
        ) : null}
        {children}
      </div>
      <div
        className="menu-btn"
        onClick={(e) => {
          if (selected) {
            setProp((props) => {
              if (menuStatus === "hide") {
                props.menuStatus = "show";
              } else {
                props.menuStatus = "hide";
              }
            });
          }
        }}
      >
        <SVG src={SVGIcons[menuIcon]} />
      </div>
    </>
  );

  const editorWidth = editorDocument?.documentElement?.clientWidth;
  const portalTop =
    portalType === "dropdown"
      ? editorDocument?.querySelector(".grigora-editor-renderer-canvas")
          ?.scrollTop +
        dom?.getBoundingClientRect()?.top +
        dom?.offsetHeight +
        (portalTopOffset ? Number(portalTopOffset) : 0)
      : 0;

  const portalContainerHeight =
    portalType === "dropdown"
      ? editorDocument?.querySelector(".grigora-editor-renderer-canvas")
          ?.scrollHeight - portalTop
      : undefined;

  return (
    <div
      {...blockProps}
      ref={(ref) => connect(drag(ref))}
      className={wrapperClass}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__Navigation(id, props)}`}</style>
      {content}
      {(menuTrigger === "desktop" ||
        (menuTrigger === "tablet" && editorWidth < 1024) ||
        (menuTrigger === "mobile" && editorWidth < 769)) &&
        editorDocument.querySelector(
          portalType === "dropdown"
            ? ".grigora-editor-renderer-canvas"
            : "body",
        ) &&
        // Portal for floating box
        createPortal(
          <>
            <div
              style={{
                top: portalTop,
                bottom: portalType === "dropdown" ? undefined : "0px",
                height: portalContainerHeight,
              }}
              className={`g-navigation-portal navigation-portal-${id} ${
                menuStatus === "show" ? "active" : ""
              }`}
              onMouseDown={(e) => {
                setProp((props) => {
                  props.menuStatus = "hide";
                });
              }}
            >
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                className="childrens"
              >
                {portalType === "dropdown" ? (
                  <></>
                ) : (
                  <>
                    <div
                      onClick={(e) => {
                        setProp((props) => {
                          props.menuStatus = "hide";
                        });
                      }}
                      className="close-icon"
                    >
                      <SVG src={SVGIcons["x-l"]} />
                    </div>
                  </>
                )}
                {children}
              </div>
            </div>
          </>,
          editorDocument.querySelector(
            portalType === "dropdown"
              ? ".grigora-editor-renderer-canvas"
              : "body",
          ),
        )}
    </div>
  );
};

// To Avoid Mangling in Production
Navigation.displayName = "Navigation";

Navigation.craft = {
  name: "Navigation",
  props: {},
  related: {
    settings: NavigationSettings,
    icon: icon,
    category: "layout",
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    supportNestedBlocks: true,
    isHiddenInPostEditor: true,
  },
  custom: {
    name: "Navigation",
    slug: "grigora/navigation",
  },
};
